import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Subscription } from 'rxjs';
import { TermosPavService, urlDosTermos } from '../../../services/termos-pav/termos-pav.service';
import { DarkModeService } from '../../../services/dark-mode/dark-mode.service';

/** #RRGGBB a partir de #RGB, #RRGGBB ou rgb()/rgba(); null se não reconhecer. */
export function corHex(valor: string | null | undefined): string | null {
  const v = (valor ?? '').trim();
  if (/^#[0-9a-f]{6}$/i.test(v)) return v.toUpperCase();
  if (/^#[0-9a-f]{3}$/i.test(v)) return ('#' + v.slice(1).split('').map(c => c + c).join('')).toUpperCase();
  const m = v.match(/^rgba?\(\s*(\d{1,3})[\s,]+(\d{1,3})[\s,]+(\d{1,3})\s*(?:[,/]\s*[\d.]+%?\s*)?\)$/i);
  if (!m) return null;
  const partes = m.slice(1, 4).map(Number);
  if (partes.some(n => n > 255)) return null;
  return '#' + partes.map(n => n.toString(16).padStart(2, '0')).join('').toUpperCase();
}

function origemDe(url: string): string {
  try { return new URL(url).origin; } catch { return ''; }
}

/** Página de termos do pav (termos-embed-fila.html) no visual da Fila. Sem paciente, abre na busca. */
@Component({
  selector: 'app-termos-pav-embed',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div *ngIf="carregando" class="te-aviso">Abrindo os termos…</div>
    <div *ngIf="erro" class="te-aviso te-erro" role="alert">
      <span>{{ erro }}</span>
      <button type="button" class="te-tentar" (click)="abrir()">Tentar de novo</button>
    </div>
    <iframe #quadro *ngIf="url" [src]="url" title="Termos do paciente" class="te-iframe"></iframe>`,
  styles: [`
    :host { display: flex; flex-direction: column; height: 100%; min-height: 0; }
    .te-aviso { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 12px;
                padding: 32px 16px; font-size: 13px; color: var(--tx-2); text-align: center; }
    .te-erro { color: #B3261E; }
    .te-tentar { height: 36px; padding: 0 16px; border-radius: 10px; border: 1px solid var(--flua-borda);
                 background: var(--cartao); color: var(--tx); font-size: 13px; font-weight: 700; }
    .te-tentar:hover { border-color: var(--marca-acao); }
    .te-iframe { flex: 1; width: 100%; min-height: 0; border: 0; background: var(--flua-fundo); }
    :host-context(.dark) .te-erro { color: #FF8A80; }`],
})
export class TermosPavEmbedComponent implements OnInit, OnDestroy {
  @Input() cdPessoaFisica?: string | number | null;
  @ViewChild('quadro') quadro?: ElementRef<HTMLIFrameElement>;

  url: SafeResourceUrl | null = null;
  carregando = false;
  erro = '';
  private origem = '';
  private sub?: Subscription;

  constructor(private termos: TermosPavService, private sanitizer: DomSanitizer, private escuro: DarkModeService) {}

  ngOnInit(): void {
    this.sub = this.escuro.mudou$?.subscribe(e => this.avisarTema(e));
    void this.abrir();
  }

  ngOnDestroy(): void { this.sub?.unsubscribe(); }

  async abrir(): Promise<void> {
    this.carregando = true;
    this.erro = '';
    this.url = null;
    try {
      const acesso = await this.termos.acesso();
      this.origem = origemDe(acesso.url);
      const endereco = urlDosTermos(acesso, {
        marca: location.hostname,
        cdPessoaFisica: this.cdPessoaFisica,
        superficie: 'fila',
        tema: this.escuro.isDarkMode() ? 'escuro' : 'claro',
        acao: corHex(getComputedStyle(document.documentElement).getPropertyValue('--marca-acao')),
      });
      // A URL vem do back da Fila (config Pav:Url), não de entrada do usuário.
      this.url = this.sanitizer.bypassSecurityTrustResourceUrl(endereco);
    } catch (e: any) {
      this.erro = e?.message || 'Não foi possível abrir os termos agora.';
    } finally {
      this.carregando = false;
    }
  }

  private avisarTema(escuro: boolean): void {
    const janela = this.quadro?.nativeElement.contentWindow;
    if (!janela || !this.origem) return;
    // A ação vai junto: na Super ela muda com o tema.
    const acao = corHex(getComputedStyle(document.documentElement).getPropertyValue('--marca-acao'));
    janela.postMessage({ type: 'fila_tema', tema: escuro ? 'escuro' : 'claro', ...(acao ? { acao } : {}) }, this.origem);
  }
}
